import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import throttle from 'lodash/throttle';
import {InputAdornment} from '@mui/material';
import {connect} from 'react-redux';
import {styled} from '@mui/material/styles';
import {toLonLat} from 'ol/proj';
import {useTranslation} from 'react-i18next';

import SearchInputOption from './search-input-option.jsx';
import {
  SOURCE_TYPES,
  geocode,
  getSuggestions,
  mapLatLonSuggestions,
  searchMgrs,
  suggestFromGoogleMapsUrl,
  suggestLatLon,
  suggestSavedSearches,
  suggestSearchItemIds,
} from '@explorer/util/search';
import {TOUR_STAGES} from '@explorer/constants';
import {
  clearSearch,
  executeSearch,
  loadSearch,
  searchSingleItem,
} from '@explorer/actions/search';
import {extentToPolygon} from '@explorer/util/geom';
import {getAvailableItemTypes} from '@explorer/selectors/item-types';
import {hydrateSearchGeometry} from '@explorer/util/granero-resolver';
import {notify} from '@explorer/actions/notifications.js';
import {trackEvent} from '@explorer/analytics';
import {useGetSavedSearchesQuery} from '@explorer/api/apiSlice.js';

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
});

const StyledAutocomplete = styled(Autocomplete)({
  '& .MuiAutocomplete-clearIndicatorDirty, & .MuiAutocomplete-clearIndicator': {
    visibility: 'visible',
  },
  '& .MuiAutocomplete-input': {
    paddingRight: '30px !important', // this seems like a bug, size small overrides this
  },
});

const SearchInput = ({
  notify,
  clearSearch,
  loadSearch,
  searchSingleItem,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [center, setCenter] = useState();

  const {data: savedSearches} = useGetSavedSearchesQuery(undefined, {
    skip: !inputValue,
  });

  const {t} = useTranslation();

  const getOptionLabel = useCallback(
    option => {
      if (option.source === SOURCE_TYPES.LATLON) {
        return t('searchInput.latLonName', option.context);
      } else if (
        option.source === SOURCE_TYPES.MGRS_POINT ||
        option.source === SOURCE_TYPES.MGRS_SQUARE
      ) {
        return t(`searchInput.${option.source}`, {
          query: option.query,
        });
      }
      return option.description || '';
    },
    [t]
  );

  useEffect(() => {
    setCenter(toLonLat(props.center));
  }, [props.center]);

  useEffect(() => {
    if (value) {
      const newValue = getOptionLabel(value);
      setInputValue(newValue);
    }
  }, [getOptionLabel, value]);

  useEffect(() => {
    if (props.searchName) {
      setInputValue(props.searchName);
      setOptions([]);
    }
  }, [props.searchName]);

  const {executeSearch} = props;
  const searchSuggestions = useCallback(
    query => {
      if (query.length < 3) {
        return [];
      }
      return getSuggestions(center, query)
        .then(response => response.features)
        .then(suggestions =>
          suggestions.map(suggestion => ({
            ...suggestion,
            onSelect: () => {
              geocode(center, suggestion.query)
                .then(({features}) => {
                  return features[0];
                })
                .then(feature => {
                  const bboxPoly = extentToPolygon({
                    left: feature.bbox[0],
                    bottom: feature.bbox[1],
                    right: feature.bbox[2],
                    top: feature.bbox[3],
                  });
                  return {feature, geometry: bboxPoly};
                })
                .then(({geometry}) => {
                  executeSearch({
                    geometry: geometry,
                    description: suggestion.description,
                    showCenterMarker:
                      suggestion.suggestion.__type === 'Address',
                  });
                });
            },
          }))
        );
    },
    [center, executeSearch]
  );

  const selectResult = async (event, result) => {
    setValue(result);

    if (result) {
      trackEvent('Search Result selected', {
        query: result.query,
        isSavedSearch: !!result.savedSearch,
        source: result.source,
        featureClass: result.suggestion?.__type,
      });

      if (result.savedSearch) {
        const hydratedSearch = await hydrateSearchGeometry(result.savedSearch);
        loadSearch(hydratedSearch);
      } else if (result.searchItem) {
        searchSingleItem(result.searchItem);
      } else if (result.onSelect) {
        result.onSelect();
      } else {
        props.executeSearch(result);
      }
    }
  };

  const search = useMemo(
    () =>
      throttle((query, callback) => {
        const requests = [
          searchMgrs(query),
          suggestSavedSearches(query, savedSearches),
          suggestSearchItemIds(query, props.itemTypes),
          suggestLatLon(query).map(mapLatLonSuggestions),
          searchSuggestions(query),
          suggestFromGoogleMapsUrl(query).map(mapLatLonSuggestions),
        ];
        Promise.all(requests)
          .then(allResults => {
            const results = [].concat.apply([], allResults);
            callback(results);
          })
          .catch(() => {
            setLoading(false);
            notify({
              message: t('searchInput.errorSearch'),
              severity: 'error',
            });
          });
      }, 1000),
    [savedSearches, props.itemTypes, searchSuggestions, notify, t]
  );

  const handleInputChange = (event, newValue, reason) => {
    setInputValue(newValue);

    if (reason === 'clear') {
      setOptions([]);
      clearSearch();
    } else if (reason === 'input') {
      if (newValue) {
        setLoading(true);
        search(newValue, results => {
          if (results) {
            setOptions(results.slice(0, 10));
          }
          setLoading(false);
        });
      }
    }
  };

  const handleInputBlur = () => {
    setInputValue(inputValue || props.searchName);
  };

  return (
    <Paper elevation={2} data-tour={TOUR_STAGES.SEARCH_INPUT}>
      <StyledAutocomplete
        freeSolo
        fullWidth
        autoHighlight
        noOptionsText={t('searchInput.noResults')}
        loading={loading}
        isOptionEqualToValue={(option, value) => {
          return option.query === value.query;
        }}
        getOptionLabel={getOptionLabel}
        filterOptions={x => x}
        options={options}
        autoComplete
        includeInputInList
        inputValue={inputValue || ''}
        value={value}
        onChange={selectResult}
        onInputChange={handleInputChange}
        renderOption={({id, ...props}, option) => (
          <SearchInputOption {...props} key={id} option={option} />
        )}
        renderInput={params => (
          <StyledTextField
            {...params}
            margin="none"
            placeholder={t('searchInput.prompt')}
            onBlur={handleInputBlur}
            inputProps={{
              ...params.inputProps,
              'data-qe': 'search-input',
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment:
                params.InputProps.endAdornment &&
                React.cloneElement(params.InputProps.endAdornment, {
                  'data-qe': `clear-search-button-${
                    value ? 'visible' : 'hidden'
                  }`,
                }),
            }}
          />
        )}
        sx={{
          '&.MuiAutocomplete-paper': {
            marginTop: 1,
          },
        }}
      />
    </Paper>
  );
};

SearchInput.propTypes = {
  center: PropTypes.array.isRequired,
  itemTypes: PropTypes.array.isRequired,
  searchName: PropTypes.string,
  clearSearch: PropTypes.func,
  executeSearch: PropTypes.func,
  loadSearch: PropTypes.func,
  notify: PropTypes.func,
  searchSingleItem: PropTypes.func,
  tour: PropTypes.bool,
};

const mapStateToProps = state => ({
  center: state.map.center,
  itemTypes: getAvailableItemTypes(state).types,
  searchName: state.search.name,
});

const mapDispatchProps = {
  clearSearch,
  executeSearch,
  loadSearch,
  notify,
  searchSingleItem,
};
export default connect(mapStateToProps, mapDispatchProps)(SearchInput);
